import { FC, ReactNode } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import StepContent from '@material-ui/core/StepContent';
import { Box, Switch, Typography } from '@material-ui/core';

const useStyles = makeStyles(theme => ({
  root: {
    padding: `${theme.spacing(2)}px 0px`,
  },
  stepLabelContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  stepNumberIcon: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.getContrastText(theme.palette.primary.main),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
  },
  stepContent: {
    borderLeft: 'none',
    marginLeft: 0,
    paddingLeft: 0,
  },
}));

export interface WbToggleableStepperProps {
  steps: Array<{
    label: string;
    content: ReactNode;
    completed: boolean;
    hasValue: boolean;
  }>;
  onToggleChange?: (stepIndex: number) => void;
  stepsAreDisabled?: boolean;
}

export const WbToggleableStepper: FC<WbToggleableStepperProps> = ({
  steps,
  stepsAreDisabled = false,
  onToggleChange,
}) => {
  const classes = useStyles();

  const StepNumberIcon: FC<{ icon: ReactNode }> = ({ icon }) => {
    return (
      <Typography variant="body2" className={classes.stepNumberIcon}>
        {icon}
      </Typography>
    );
  };

  return (
    <Stepper
      orientation="vertical"
      connector={<div />}
      activeStep={-1}
      alternativeLabel={false}
      className={classes.root}
    >
      {steps.map((s, idx: number) => (
        <Step key={idx} expanded completed={s.completed ? true : undefined}>
          <StepLabel StepIconComponent={StepNumberIcon}>
            <Box className={classes.stepLabelContainer}>
              <Typography variant="body1">{s.label}</Typography>
              {stepsAreDisabled && idx !== 0 && (
                <Switch
                  inputProps={{ 'aria-label': `disable step ${idx}` }}
                  disabled={!s.hasValue || !steps[idx - 1].hasValue}
                  checked={!!s.completed}
                  onChange={() => onToggleChange?.(idx)}
                />
              )}
            </Box>
          </StepLabel>

          <StepContent classes={{ root: classes.stepContent }}>
            {s.content}
          </StepContent>
        </Step>
      ))}
    </Stepper>
  );
};
